import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Cookie, Info, Clock, Settings, Shield, Chrome, Globe } from 'lucide-react';
import SEO from '../components/SEO';

export const Cookies = () => {
  const { t } = useTranslation();

  // Get browsers array with default empty array if not found
  const browsers = t('cookies.howToDisable.browsers.items', { returnObjects: true, defaultValue: [] });

  return (
    <div className="pt-24 pb-12 px-4 max-w-4xl mx-auto">
      <SEO
        title={`${t('navigation.legal.cookies') || 'Cookies'} – Victor Lazari`}
        description={t('cookies.title') as string}
        canonical={"https://victorlazari.com/cookies"}
        image={"https://raw.githubusercontent.com/VictorLazari-lab/victorlazari.com/VictorLazari-Media/varienda%20minimalist%20logo.png"}
        type="website"
      />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-12"
      >
        <h1 className="text-4xl md:text-5xl font-semibold text-slate-900 flex items-center justify-center gap-3">
          <Cookie className="w-10 h-10" />
          {t('cookies.title')}
        </h1>
      </motion.div>

      <div className="space-y-8">
        {/* Introduction */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <Info className="w-6 h-6 text-cyan-400" />
            <h2 className="text-xl font-semibold text-cyan-400">
              {t('cookies.intro.title')}
            </h2>
          </div>
          <p className="text-slate-600">{t('cookies.intro.content')}</p>
        </motion.section>

        {/* What Are Cookies */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <Cookie className="w-6 h-6 text-cyan-400" />
            <h2 className="text-xl font-semibold text-cyan-400">
              {t('cookies.whatAreCookies.title')}
            </h2>
          </div>
          <p className="text-slate-600">{t('cookies.whatAreCookies.content')}</p>
        </motion.section>

        {/* Types of Cookies */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <Clock className="w-6 h-6 text-cyan-400" />
            <h2 className="text-xl font-semibold text-cyan-400">
              {t('cookies.types.title')}
            </h2>
          </div>
          <p className="text-slate-600 mb-6">{t('cookies.types.content')}</p>
          
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium text-cyan-400 mb-2">
                {t('cookies.types.session.title')}
              </h3>
              <p className="text-slate-600">{t('cookies.types.session.content')}</p>
            </div>
            <div>
              <h3 className="text-lg font-medium text-cyan-400 mb-2">
                {t('cookies.types.firstParty.title')}
              </h3>
              <p className="text-slate-600">{t('cookies.types.firstParty.content')}</p>
            </div>
          </div>
        </motion.section>

        {/* Cookies We Use */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6 text-cyan-400" />
            <h2 className="text-xl font-semibold text-cyan-400">
              {t('cookies.cookiesWeUse.title')}
            </h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium text-cyan-400 mb-2">
                {t('cookies.cookiesWeUse.necessary.title')}
              </h3>
              <p className="text-slate-600">{t('cookies.cookiesWeUse.necessary.content')}</p>
            </div>
            <div>
              <h3 className="text-lg font-medium text-cyan-400 mb-2">
                {t('cookies.cookiesWeUse.navigation.title')}
              </h3>
              <p className="text-slate-600">{t('cookies.cookiesWeUse.navigation.content')}</p>
            </div>
            <div>
              <h3 className="text-lg font-medium text-cyan-400 mb-2">
                {t('cookies.cookiesWeUse.thirdParty.title')}
              </h3>
              <p className="text-slate-600">{t('cookies.cookiesWeUse.thirdParty.content')}</p>
            </div>
          </div>
        </motion.section>

        {/* Your Consent */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <Globe className="w-6 h-6 text-cyan-400" />
            <h2 className="text-xl font-semibold text-cyan-400">
              {t('cookies.consent.title')}
            </h2>
          </div>
          <p className="text-slate-600 mb-2">{t('cookies.consent.content')}</p>
          <p className="text-sky-700">{t('cookies.consent.status')}</p>
        </motion.section>

        {/* How to Disable */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <Settings className="w-6 h-6 text-cyan-400" />
            <h2 className="text-xl font-semibold text-cyan-400">
              {t('cookies.howToDisable.title')}
            </h2>
          </div>
          <p className="text-slate-600 mb-6">{t('cookies.howToDisable.content')}</p>
          
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium text-cyan-400 mb-2">
                {t('cookies.howToDisable.browsers.title')}
              </h3>
              {Array.isArray(browsers) && browsers.length > 0 && (
                <ul className="list-disc list-inside text-slate-600 space-y-1">
                  {browsers.map((browser, index) => 
                    typeof browser === 'string' ? (
                      <li key={index}>{browser}</li>
                    ) : null
                  )}
                </ul>
              )}
            </div>
            <div>
              <h3 className="text-lg font-medium text-cyan-400 mb-2">
                {t('cookies.howToDisable.analytics.title')}
              </h3>
              <p className="text-slate-600">{t('cookies.howToDisable.analytics.content')}</p>
            </div>
          </div>
        </motion.section>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-sm text-slate-500 text-center mt-8"
        >
          {t('cookies.lastUpdated')}
        </motion.p>
      </div>
    </div>
  );
};